import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { apiFetch } from '../api';

// Page d'activation atteinte via le lien d'email (?token=...). Le membre voit ses
// données MedReg (lecture seule), complète son profil et définit son mot de passe.
// backend/onboarding/verify.php (chargement) puis submit.php (envoi).
const Activation = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [verifying, setVerifying] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  // Champs du formulaire
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [biography, setBiography] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [photo, setPhoto] = useState(null);
  const [selectedLangs, setSelectedLangs] = useState([]);
  const [selectedCliniques, setSelectedCliniques] = useState([]);

  useEffect(() => {
    const verify = async () => {
      if (!token) {
        setError('Lien incomplet.');
        setVerifying(false);
        return;
      }
      try {
        const res = await apiFetch(`/backend/onboarding/verify.php?token=${encodeURIComponent(token)}`);
        if (res.success) {
          setData(res.data);
          setContactEmail(res.data.login_email || '');
        } else {
          setError(res.message || 'Lien invalide ou expiré.');
        }
      } catch (err) {
        setError('Erreur lors de la vérification du lien.');
      } finally {
        setVerifying(false);
      }
    };
    verify();
  }, [token]);

  const toggle = (list, setList, id) =>
    setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    if (password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères.');
      return;
    }
    if (password !== confirm) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('token', token);
      fd.append('password', password);
      if (biography) fd.append('biography', biography);
      if (contactEmail) fd.append('contact_email', contactEmail);
      if (website) fd.append('website', website);
      if (photo) fd.append('profile_picture', photo);
      fd.append('languages', JSON.stringify(selectedLangs));
      fd.append('cliniques', JSON.stringify(selectedCliniques));

      const res = await apiFetch('/backend/onboarding/submit.php', { method: 'POST', body: fd });
      if (res.success) {
        setMessage(res.message || 'Profil enregistré.');
      } else {
        setError(res.message || 'Une erreur est survenue.');
      }
    } catch (err) {
      setError('Erreur serveur. Réessayez plus tard.');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <Container className="my-5 text-center">
        <Spinner animation="border" /> <span className="ms-2">Vérification du lien…</span>
      </Container>
    );
  }

  if (!data && error) {
    return (
      <Container className="my-5">
        <Row className="justify-content-center">
          <Col md={6}>
            <Card><Card.Body>
              <h4>Lien invalide</h4>
              <Alert variant="danger">{error}</Alert>
              <p>Ce lien d'activation est invalide ou a expiré. Demandez-en un nouveau depuis « Activer mon compte ».</p>
            </Card.Body></Card>
          </Col>
        </Row>
      </Container>
    );
  }

  if (message) {
    return (
      <Container className="my-5">
        <Row className="justify-content-center">
          <Col md={6}>
            <Card><Card.Body className="text-center">
              <h4>Merci !</h4>
              <Alert variant="success">{message}</Alert>
            </Card.Body></Card>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container className="my-5">
      <Row className="justify-content-center">
        <Col md={8}>
          <Card>
            <Card.Body>
              <Card.Title>Compléter mon profil — {data.first_name} {data.name}</Card.Title>

              {/* Données MedReg en lecture seule */}
              <Alert variant="light" className="border">
                <strong>Informations du registre fédéral (non modifiables) :</strong>
                {data.specialties && data.specialties.length > 0 && (
                  <div>Spécialités : {data.specialties.join(', ')}</div>
                )}
                {data.addresses && data.addresses.length > 0 && (
                  <div>
                    Adresse(s) :{' '}
                    {data.addresses.map((a, i) => (
                      <span key={i}>{[a.street_with_number, a.zip, a.city].filter(Boolean).join(' ')}{i < data.addresses.length - 1 ? ' ; ' : ''}</span>
                    ))}
                  </div>
                )}
              </Alert>

              {error && <Alert variant="danger">{error}</Alert>}

              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Mot de passe (8 caractères minimum)</Form.Label>
                  <Form.Control type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Confirmer le mot de passe</Form.Label>
                  <Form.Control type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Email de contact (affiché au public)</Form.Label>
                  <Form.Control type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Biographie</Form.Label>
                  <Form.Control as="textarea" rows={3} value={biography} onChange={(e) => setBiography(e.target.value)} />
                  <Form.Text className="text-muted">Présentez-vous (parcours, expertise…). Optionnel.</Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Site web</Form.Label>
                  <Form.Control type="url" placeholder="https://…" value={website} onChange={(e) => setWebsite(e.target.value)} />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Photo de profil</Form.Label>
                  <Form.Control type="file" accept="image/*" onChange={(e) => setPhoto(e.target.files[0])} />
                  <Form.Text className="text-muted">Visage centré, l'image s'affichera en carré. Optionnel.</Form.Text>
                </Form.Group>

                {data.languages && data.languages.length > 0 && (
                  <Form.Group className="mb-3">
                    <Form.Label>Langues parlées</Form.Label>
                    {data.languages.map((l) => (
                      <Form.Check
                        key={l.id}
                        type="checkbox"
                        id={`lang-${l.id}`}
                        label={l.name_fr}
                        checked={selectedLangs.includes(l.id)}
                        onChange={() => toggle(selectedLangs, setSelectedLangs, l.id)}
                      />
                    ))}
                  </Form.Group>
                )}

                {data.cliniques && data.cliniques.length > 0 && (
                  <Form.Group className="mb-3">
                    <Form.Label>Cliniques où vous travaillez</Form.Label>
                    {data.cliniques.map((c) => (
                      <Form.Check
                        key={c.id}
                        type="checkbox"
                        id={`clinique-${c.id}`}
                        label={c.name}
                        checked={selectedCliniques.includes(c.id)}
                        onChange={() => toggle(selectedCliniques, setSelectedCliniques, c.id)}
                      />
                    ))}
                  </Form.Group>
                )}

                <div className="d-flex justify-content-end">
                  <Button type="submit" variant="primary" disabled={loading}>
                    {loading ? 'Enregistrement…' : 'Valider mon profil'}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Activation;
